import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api } from '../lib/api'
import { exportAudit, exportTranscript } from '../lib/exports'
import { brl, clock, pct } from '../lib/format'
import { useCall } from '../lib/useCall'
import { color, radius, shadow } from '../theme/tokens'
import type {
  AudioSource,
  Briefing as BriefingData,
  Case,
  CallSummary,
  Policy,
  PolicyVersion,
  RealtimeStatus,
  Scenario,
} from '../lib/types'
import { Header, SideNav } from '../components/Shell'
import { AuditorBand } from '../components/demo/AuditorBand'
import { Briefing } from '../components/demo/Briefing'
import { CasePanel } from '../components/demo/CasePanel'
import { NegotiationEnvelope } from '../components/demo/NegotiationEnvelope'
import { Orb } from '../components/demo/Orb'
import { QueuePanel } from '../components/demo/QueuePanel'
import { Transcript } from '../components/demo/Transcript'
import { Button, Eyebrow, Check } from '../components/ui'

type Mode = 'exec' | 'aud'

const SOURCE_PILLS: Record<AudioSource, string> = {
  sim: 'áudio simulado',
  mic: 'microfone da sala',
  rtc: 'agente de voz · Modelo A',
  pipeline: 'agente de voz',
  wa: 'WhatsApp · texto',
}

export function DemoAoVivo() {
  const [mode, setMode] = useState<Mode>('exec')
  const [bandHeight, setBandHeight] = useState(320)
  const [scenarioId, setScenarioId] = useState('2')
  const [source, setSource] = useState<AudioSource>('sim')
  const [speed] = useState(1)

  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [cases, setCases] = useState<Case[]>([])
  const [briefing, setBriefing] = useState<BriefingData | null>(null)
  const [policy, setPolicy] = useState<Policy | null>(null)
  const [versions, setVersions] = useState<PolicyVersion[]>([])
  const [realtime, setRealtime] = useState<RealtimeStatus | null>(null)
  const [summary, setSummary] = useState<CallSummary | null>(null)
  const [composer, setComposer] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)

  const caseId = useMemo(
    () => scenarios.find((item) => item.id === scenarioId)?.case_id ?? 'maria-fernandes',
    [scenarios, scenarioId],
  )

  const call = useCall({ caseId, scenarioId, source, speed })
  const { state } = call

  // Carga inicial.
  useEffect(() => {
    Promise.all([
      api.listScenarios(),
      api.listCases(),
      api.getPublished(),
      api.listVersions(),
      api.realtimeStatus(),
    ])
      .then(([scenarioList, caseList, publishedPolicy, versionList, realtimeStatus]) => {
        setScenarios(scenarioList)
        setCases(caseList)
        setPolicy(publishedPolicy)
        setVersions(versionList)
        setRealtime(realtimeStatus)
      })
      .catch((error: unknown) =>
        setLoadError(error instanceof Error ? error.message : 'Falha ao carregar a demonstração'),
      )
  }, [])

  // O briefing muda com o cenário selecionado.
  useEffect(() => {
    if (!caseId) return
    api
      .briefing(caseId, scenarioId)
      .then(setBriefing)
      .catch(() => setBriefing(null))
  }, [caseId, scenarioId])

  const debtor = useMemo(
    () => cases.find((item) => item.id === caseId) ?? briefing?.case ?? null,
    [cases, caseId, briefing],
  )

  const publishedVersion = useMemo(
    () => versions.find((v) => v.version === policy?.version) ?? versions[versions.length - 1],
    [versions, policy],
  )

  // O encerramento pode ser disparado pelo botão e pelo fim do roteiro ao mesmo
  // tempo; a trilha não pode receber dois encerramentos para a mesma chamada.
  const ending = useRef(false)

  const hangup = useCallback(async () => {
    if (ending.current) return
    ending.current = true
    await call.hangup()
    if (!call.callId) return
    try {
      const result = await api.endCall(call.callId, {
        duration: clock(state.elapsed),
        discount_granted_pct: state.offerPct ?? 0,
        client_initial_ask_pct: state.clientFirstAskPct,
        recovered_value: state.proposal?.total ?? 0,
        result: state.proposal ? 'Acordo fechado · com entrada' : 'Sem acordo',
        suggested_tabulation: state.proposal ? 'Acordo com Entrada' : 'Sem Contato Efetivo',
      })
      setSummary(result)
    } catch {
      setSummary(null)
    }
  }, [call, state])

  // O roteiro chama hangup ao terminar; capturamos a transição para gerar o resumo.
  useEffect(() => {
    if (state.phase === 'ended' && !summary && call.callId) {
      void hangup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase])

  const reset = useCallback(() => {
    ending.current = false
    setSummary(null)
    call.reset()
  }, [call])

  const whisper = useCallback(() => {
    const text = window.prompt('Sussurrar para a Alice (o cliente não ouve):')
    if (text) call.whisper(text)
  }, [call])

  const sendComposer = useCallback(() => {
    const text = composer.trim()
    if (!text) return
    call.sendClientText(text)
    setComposer('')
  }, [composer, call])

  const auditorOpen = mode === 'aud' && state.phase !== 'briefing'
  // No modo espelho a chamada só é de fato "ao vivo" depois que o aparelho atende.
  const awaitingLine =
    call.isMirroring && (call.lineStatus === 'ringing' || call.lineStatus === 'connecting')
  const modalityLabel =
    state.capPct <= 0
      ? 'Fora da matriz · escalonar para a mesa de recuperação'
      : state.modality === 'vista'
        ? `À vista · teto ${pct(state.capPct)}`
        : `Entrada + parcelas · teto ${pct(state.capPct)}`

  if (loadError) {
    return (
      <ErrorScreen
        message={loadError}
        hint="Confirme que o backend está no ar: uvicorn app.main:app --reload --port 8000"
      />
    )
  }

  if (!briefing || !debtor || !policy) {
    return <LoadingScreen />
  }

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        background: color.bg,
      }}
    >
      <Header subtitle="Console de supervisão" badge="Banco Vantagem · slot de logo">
        {state.phase === 'live' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(237,79,46,.14)',
              border: '1px solid rgba(237,79,46,.45)',
              borderRadius: radius.pill,
              padding: '5px 12px 5px 10px',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: color.redLive,
                animation: 'lv 1.4s ease-in-out infinite',
              }}
            />
            <span
              style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: '#ffd9cf' }}
            >
              AO VIVO
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {clock(state.elapsed)}
            </span>
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            background: color.panelRaised,
            border: `1px solid ${color.panelBorder}`,
            borderRadius: radius.lg,
            padding: 3,
            gap: 2,
          }}
          role="group"
          aria-label="Modo de visualização"
        >
          {(['exec', 'aud'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              style={{
                border: 'none',
                borderRadius: radius.md,
                padding: '7px 14px',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all .2s ease',
                background: mode === value ? color.brand : 'transparent',
                color: mode === value ? '#fff' : color.panelTextMuted,
              }}
            >
              {value === 'exec' ? 'Executivo' : 'Auditor'}
            </button>
          ))}
        </div>
      </Header>

      <div
        style={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          transition: 'padding-bottom .42s cubic-bezier(.22,.61,.36,1)',
          paddingBottom: auditorOpen ? bandHeight : 0,
        }}
      >
        <SideNav />

        <QueuePanel
          cases={cases}
          activeCaseId={caseId}
          queueState={
            state.phase === 'live' ? 'em chamada' : state.phase === 'ended' ? 'encerrada' : 'aguardando'
          }
          onSelect={(nextCaseId) => {
            const scenario = scenarios.find((item) => item.case_id === nextCaseId)
            if (scenario && state.phase === 'briefing') setScenarioId(scenario.id)
          }}
        />

        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 12,
            gap: 12,
            minHeight: 0,
          }}
        >
          {call.error ? (
            <div
              role="status"
              style={{
                background: color.amberTint,
                border: `1px solid ${color.brandBorder}`,
                color: color.amberInk,
                borderRadius: radius.lg,
                padding: '10px 14px',
                fontSize: 12.5,
                flex: 'none',
              }}
            >
              {call.error}
            </div>
          ) : null}

          {state.phase === 'briefing' ? (
            <Briefing
              data={briefing}
              scenarios={scenarios}
              source={source}
              scenarioId={scenarioId}
              realtime={realtime}
              starting={call.connecting}
              linePeers={call.linePeers}
              onSourceChange={setSource}
              onScenarioChange={setScenarioId}
              onStart={() => void call.start()}
            />
          ) : null}

          {state.phase === 'live' ? (
            <>
              <div
                style={{
                  flex: 1,
                  background: color.panel,
                  borderRadius: radius.panel,
                  display: 'flex',
                  minHeight: 0,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: 300,
                    flex: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 20,
                    padding: 20,
                    borderRight: '1px solid rgba(255,255,255,.08)',
                    position: 'relative',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'radial-gradient(70% 60% at 50% 42%, rgba(240,134,47,.1), transparent 72%)',
                    }}
                  />
                  <Orb state={state.orb} amplitudeRef={call.amplitudeRef} />

                  <div style={{ position: 'relative', textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                      {awaitingLine
                        ? call.lineStatus === 'connecting'
                          ? 'Atendida · estabelecendo áudio'
                          : 'Chamando a linha da cliente…'
                        : state.humanInControl
                          ? 'Operador humano no comando'
                          : state.orb === 'ai'
                            ? `${policy.persona.name} está falando`
                            : state.orb === 'cli'
                              ? `${debtor.name.split(' ')[0]} está falando`
                              : 'Processando'}
                    </div>
                    <div
                      style={{ fontSize: 11.5, color: color.panelTextFaint, marginTop: 3 }}
                    >
                      {awaitingLine
                        ? 'o áudio roda na aba /linha — este console é o espelho'
                        : state.muted
                          ? 'linha em mudo'
                          : state.orb === 'ai'
                            ? 'síntese de voz em streaming'
                            : state.orb === 'cli'
                              ? 'transcrição em streaming'
                              : 'consultando política e histórico'}
                    </div>
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 7,
                      width: '100%',
                      padding: '0 6px',
                    }}
                  >
                    <MetaRow label={`${policy.persona.name} · agente de cobrança`}>
                      voz {policy.persona.voice} · pt-BR
                    </MetaRow>
                    <MetaRow label={`${debtor.name} · linha`}>{debtor.phone}</MetaRow>
                    <MetaRow label="Latência ponta a ponta">
                      <span style={{ color: '#b9c6db' }}>
                        {680 + state.jitterMs}ms
                      </span>
                    </MetaRow>
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 18px',
                      borderBottom: '1px solid rgba(255,255,255,.08)',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '.09em',
                        color: color.panelTextFaint,
                      }}
                    >
                      TRANSCRIÇÃO AO VIVO
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 11,
                        color: color.panelTextFaint,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={darkPill}>
                        {scenarios.find((s) => s.id === scenarioId)?.label ?? `Cenário ${scenarioId}`}
                      </span>
                      <span style={darkPill}>{call.isMirroring ? SOURCE_PILLS.rtc : SOURCE_PILLS[source]}</span>
                    </div>
                  </div>

                  {awaitingLine && state.turns.length === 0 ? (
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        color: color.panelTextFaint,
                        textAlign: 'center',
                        padding: 24,
                      }}
                    >
                      <div style={{ fontSize: 28, animation: 'lv 1.4s ease-in-out infinite' }} aria-hidden>
                        ☎
                      </div>
                      <div style={{ fontSize: 13.5, color: color.panelText }}>
                        Tocando na linha da cliente
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.6, maxWidth: 340 }}>
                        Atenda na aba <b style={{ color: color.panelText }}>/linha</b>. A transcrição
                        aparece aqui assim que o áudio conectar.
                      </div>
                    </div>
                  ) : (
                    <Transcript turns={state.turns} clientName={debtor.name} />
                  )}

                  <div
                    style={{
                      padding: '10px 18px',
                      borderTop: '1px solid rgba(255,255,255,.08)',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <input
                      value={composer}
                      onChange={(event) => setComposer(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') sendComposer()
                      }}
                      placeholder="Modo texto — digitar como a cliente (fallback se o áudio falhar na sala)"
                      aria-label="Entrada de texto como cliente"
                      style={{
                        flex: 1,
                        background: color.panelRaised,
                        border: `1px solid ${color.panelBorder}`,
                        color: '#fff',
                        borderRadius: radius.md,
                        padding: '9px 12px',
                        fontSize: 13,
                      }}
                    />
                    <Button variant="dark" onClick={sendComposer} style={{ padding: '9px 15px' }}>
                      Enviar
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ flex: 'none', display: 'flex', gap: 12, alignItems: 'stretch' }}>
                <NegotiationEnvelope
                  debt={debtor.total_debt}
                  capPct={state.capPct}
                  offerPct={state.offerPct}
                  clientAskPct={state.clientAskPct}
                  trail={state.trail}
                  modalityLabel={modalityLabel}
                  offerWall={state.offerWall}
                />

                <div
                  style={{
                    width: 360,
                    flex: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      background: color.surface,
                      border: `1px solid ${color.border}`,
                      borderLeft: `3px solid ${state.proposal ? color.green : color.border}`,
                      borderRadius: radius.xxl,
                      padding: '14px 16px',
                      boxShadow: shadow.card,
                      flex: 1,
                    }}
                  >
                    <Eyebrow style={{ display: 'block', marginBottom: 8 }}>PROPOSTA EM MESA</Eyebrow>
                    {state.proposal ? (
                      <div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '-.2px',
                          }}
                        >
                          {brl(state.proposal.total)}
                        </div>
                        <div
                          style={{
                            fontSize: 12.5,
                            marginTop: 5,
                            lineHeight: 1.5,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {describeProposal(state.proposal)}
                        </div>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 10,
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: '5px 11px',
                            borderRadius: radius.pill,
                            background: color.greenTint,
                            color: color.greenInk,
                          }}
                        >
                          <Check />
                          {state.proposal.status}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12.5, color: color.inkFaint, padding: '8px 0' }}>
                        Nenhuma proposta formalizada ainda.
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="primary"
                      onClick={call.toggleHuman}
                      style={{ flex: 1, padding: '12px 10px', fontSize: 13, fontWeight: 700 }}
                    >
                      {state.humanInControl ? 'Devolver à IA' : 'Assumir chamada'}
                    </Button>
                    <Button onClick={call.toggleMute} style={{ padding: '12px 14px' }}>
                      {state.muted ? 'Reativar' : 'Mudo'}
                    </Button>
                    <Button onClick={whisper} style={{ padding: '12px 14px' }}>
                      Sussurrar
                    </Button>
                    <Button variant="danger" onClick={() => void hangup()} style={{ padding: '12px 14px' }}>
                      Encerrar
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {state.phase === 'ended' ? (
            <SummaryView
              summary={summary}
              debtorName={debtor.name}
              duration={clock(state.elapsed)}
              onReset={reset}
              onExportTranscript={() => exportTranscript(state, debtor.name)}
              onExportAudit={() => exportAudit(state, policy.version, 'json')}
            />
          ) : null}
        </main>

        <CasePanel debtor={debtor} artifacts={state.artifacts} />
      </div>

      <AuditorBand
        open={auditorOpen}
        height={bandHeight}
        onHeightChange={setBandHeight}
        onCollapse={() => setMode('exec')}
        state={state}
        matrix={policy.matrix}
        appliedLine={briefing.caps_label}
        policyVersion={policy.version}
        publishedAt={briefing.policy_published_at}
        publishedBy={publishedVersion?.published_by ?? 'Diretoria de Crédito'}
        latency={call.script?.latency_breakdown ?? []}
      />
    </div>
  )
}

const darkPill: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,.16)',
  borderRadius: radius.pill,
  padding: '3px 9px',
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        color: color.panelTextFaint,
        gap: 8,
      }}
    >
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{children}</span>
    </div>
  )
}

function describeProposal(proposal: NonNullable<ReturnType<typeof useCall>['state']['proposal']>) {
  const parts: string[] = []
  if (proposal.down_payment) parts.push(`Entrada ${brl(proposal.down_payment)}`)
  if (proposal.installments && proposal.installment_value) {
    parts.push(`${proposal.installments}x de ${brl(proposal.installment_value)}`)
  }
  parts.push(`desconto ${pct(proposal.discount_pct)}`)
  if (proposal.first_due_date) parts.push(`1º venc. ${proposal.first_due_date}`)
  return parts.join(' · ')
}

function SummaryView({
  summary,
  debtorName,
  duration,
  onReset,
  onExportTranscript,
  onExportAudit,
}: {
  summary: CallSummary | null
  debtorName: string
  duration: string
  onReset(): void
  onExportTranscript(): void
  onExportAudit(): void
}) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.panel,
        padding: '28px 32px',
        boxShadow: shadow.card,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Eyebrow style={{ letterSpacing: '.1em' }}>RESUMO PÓS-CHAMADA</Eyebrow>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            padding: '4px 11px',
            borderRadius: radius.pill,
            background: color.greenTint,
            color: color.greenInk,
          }}
        >
          {summary?.result ?? 'Chamada encerrada'}
        </span>
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 20px', letterSpacing: '-.3px' }}>
        {debtorName} · {summary?.duration ?? duration} de chamada
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <Stat label="VALOR RECUPERADO">{brl(summary?.recovered_value ?? 0)}</Stat>
        <Stat label="DESCONTO CONCEDIDO">
          {pct(summary?.discount_granted_pct ?? 0)}{' '}
          <span style={{ fontSize: 13, color: color.inkSubtle, fontWeight: 500 }}>
            de {pct(summary?.discount_available_pct ?? 0)} disponíveis
          </span>
        </Stat>
        <Stat label="PEDIDO INICIAL DO CLIENTE" tint={color.red}>
          {summary?.client_initial_ask_pct == null ? '—' : pct(summary.client_initial_ask_pct)}
        </Stat>
        <Stat label="ARTEFATOS GERADOS">{summary?.artifact_count ?? 0}</Stat>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.inkSubtle, marginBottom: 8 }}>
            TABULAÇÃO SUGERIDA
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(summary?.tabulation_options ?? []).map((option) => {
              const selected = option === summary?.suggested_tabulation
              return (
                <span
                  key={option}
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: '8px 14px',
                    borderRadius: radius.pill,
                    border: `1.5px solid ${selected ? color.green : color.border}`,
                    background: selected ? color.greenTint : color.surface,
                    color: selected ? color.greenInk : color.inkSubtle,
                    cursor: 'pointer',
                  }}
                >
                  {option}
                </span>
              )
            })}
          </div>
          {summary ? (
            <div
              style={{
                fontSize: 12,
                color: color.inkSubtle,
                marginTop: 14,
                lineHeight: 1.6,
              }}
            >
              {summary.break_risk_note}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 240 }}>
          <Button onClick={onExportTranscript} style={{ textAlign: 'left' }}>
            Baixar transcrição completa
          </Button>
          <Button
            disabled
            title="Disponível quando a chamada roda pela Realtime API com gravação ativa"
            style={{ textAlign: 'left' }}
          >
            Baixar áudio da chamada
          </Button>
          <Button onClick={onExportAudit} style={{ textAlign: 'left' }}>
            Exportar trilha de auditoria
          </Button>
          <Button variant="primary" onClick={onReset} style={{ fontWeight: 700 }}>
            Próximo da fila →
          </Button>
        </div>
      </div>
    </div>
  )
}

function Stat({
  label,
  children,
  tint,
}: {
  label: string
  children: React.ReactNode
  tint?: string
}) {
  return (
    <div
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: radius.xl,
        padding: '14px 16px',
        background: color.surfaceMuted,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: color.inkSubtle, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: tint,
        }}
      >
        {children}
      </div>
    </div>
  )
}

function LoadingScreen() {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color.inkSubtle,
        fontSize: 13,
      }}
    >
      Carregando a demonstração…
    </div>
  )
}

function ErrorScreen({ message, hint }: { message: string; hint: string }) {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: 40,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, color: color.redInk }}>{message}</div>
      <div style={{ fontSize: 13, color: color.inkSubtle, fontFamily: "'Roboto Mono', monospace" }}>
        {hint}
      </div>
    </div>
  )
}
